interface ProcessSectionProps {
  scrollTo: (id: string) => void;
}

export default function ProcessSection({ scrollTo }: ProcessSectionProps) {
  const steps = [
    { title: 'Диагностика', desc: 'Определяем текущие проблемы и зоны роста' },
    { title: 'Аудит', desc: 'Анализируем компетенции команды и бизнес-процессы' },
    { title: 'Подготовка программы', desc: 'Разрабатываем индивидуальную программу под задачи' },
    { title: 'Обучение', desc: 'Проводим интерактивные занятия с практикой' },
    { title: 'Практика', desc: 'Отрабатываем навыки на реальных кейсах компании' },
    { title: 'Сопровождение', desc: 'Поддерживаем внедрение новых навыков в работу' },
    { title: 'Оценка результатов', desc: 'Измеряем бизнес-показатели и корректируем программу' }
  ];

  const whyNotWorkCards = [
    {
      icon: 'ri-close-circle-line',
      title: 'Без подготовки',
      desc: 'Тренинг проводится без предварительной диагностики потребностей бизнеса и аудита компетенций команды. Результат — программа не попадает в реальные проблемы.'
    },
    {
      icon: 'ri-tools-line',
      title: 'Без практики',
      desc: 'Теория без отработки на реальных кейсах компании быстро забывается. Сотрудники возвращаются к привычным моделям поведения.'
    },
    {
      icon: 'ri-user-unfollow-line',
      title: 'Без сопровождения',
      desc: 'После тренинга никто не поддерживает изменения: нет посттренинговых встреч, нет контроля внедрения, нет обратной связи.'
    },
    {
      icon: 'ri-survey-line',
      title: 'Без контроля',
      desc: 'Результаты не измеряются — непонятно, изменилось ли что-то в работе сотрудников. Нет KPI для оценки эффективности обучения.'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Без изменения системы',
      desc: 'Сотрудников обучили, но бизнес-процессы остались прежними. Старая система не даёт применять новые навыки — результат обнуляется.'
    }
  ];

  return (
    <>
      {/* Из чего состоит эффективное корпоративное обучение */}
      <section id="process" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 text-center mb-6">
            Из чего состоит эффективное корпоративное обучение
          </h2>
          <p className="text-lg text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            Семь этапов, которые превращают обучение из формальности в реальный инструмент роста бизнеса
          </p>

          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col gap-0">
              {steps.map((step, i) => (
                <div key={i}>
                  <div className="flex md:flex-col items-center gap-4 md:gap-0 md:text-center">
                    <div className="flex-shrink-0 w-14 h-14 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {i + 1}
                    </div>
                    <div className="md:mt-3 flex-1">
                      <h3 className="font-bold text-gray-900 text-sm md:text-base">{step.title}</h3>
                      <p className="text-gray-500 text-xs md:text-sm mt-1 hidden md:block">{step.desc}</p>
                    </div>
                    {/* Desktop arrow */}
                    {i < steps.length - 1 && (
                      <div className="hidden md:block text-teal-400 mx-1">
                        <i className="ri-arrow-down-line text-xl"></i>
                      </div>
                    )}
                  </div>
                  {/* Mobile arrow between steps */}
                  {i < steps.length - 1 && (
                    <div className="flex md:hidden justify-center py-1">
                      <div className="text-teal-400 ml-7">
                        <i className="ri-arrow-down-line text-xl"></i>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="max-w-3xl mx-auto mt-14 bg-teal-50 border border-teal-200 rounded-xl p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Почему большинство тренингов не работают</h3>
            <p className="text-gray-700 leading-relaxed">
              Главная причина — отсутствие системы. Компании заказывают разовый тренинг, пропуская этапы диагностики, аудита и сопровождения. Сотрудники получают порцию знаний, но без практики и поддержки эти знания не превращаются в навыки. Через 2-3 недели после тренинга 80% информации забывается, и поведение возвращается к прежнему. Именно поэтому <strong>корпоративное обучение</strong> должно быть процессом, а не событием.
            </p>
            <div className="mt-4 text-sm text-teal-700">
              <i className="ri-arrow-right-line mr-1"></i>
              Для развития навыков менеджеров рекомендуем посмотреть страницу{' '}
              <a href="/enciklopediya-korporativnogo-obucheniya/obuchenie-otdela-prodazh" className="font-semibold underline hover:text-teal-800">Обучение отдела продаж</a>.
            </div>
          </div>
        </div>
      </section>

      {/* Почему разовые тренинги редко дают долгосрочный результат */}
      <section className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 text-center mb-6">
            Почему разовые тренинги редко дают долгосрочный результат
          </h2>
          <p className="text-lg text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            Пять причин, почему точечные мероприятия не приводят к устойчивым изменениям
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whyNotWorkCards.map((card, i) => (
              <div key={i} className="bg-white p-7 rounded-xl border border-gray-100 hover:border-teal-200 transition-colors">
                <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${card.icon} text-2xl text-red-500`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{card.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
          <div className="max-w-3xl mx-auto mt-12 text-center">
            <div className="bg-teal-600 text-white rounded-xl p-8">
              <p className="text-lg md:text-xl font-semibold leading-relaxed">
                Именно поэтому эффективное <strong>корпоративное обучение</strong> — это всегда система, включающая диагностику, программу, практику, сопровождение, контроль и оценку результатов. Только такой подход даёт измеримые бизнес-результаты.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}